RivalIQ.apps.SocialPosts.module('widgets', function (widgets, App, Backbone, Marionette, $, _, s) {

    'use strict';

    const YourActivityNameCell = Marionette.ItemView.extend({
        tagName: 'td',
        template: '#socialPostsDimensionAnalysisYourActivityRow',
        className: 'metric-table-cell text-left',

        templateHelpers: function () {
            const me = this;

            const child = {};
            child[me.model.get('dimension')] = undefined;
            const metric = me.model.build(child);

            return {
                data: me.getOption('data'),
                metric: metric
            };
        }
    });

    function perDayFormat(data) {
        return `${RivalIQ.formatter.formatAbbreviated(_.last(data))} / day`;
    }

    function frequencyText(dimension, metric, value) {
        if (dimension === 'channel') {
            return ['Your most active channel is ', metric.format(value), '.'].join('');
        }

        return [
            'Your most common post ',
            dimension,
            ' is ',
            metric.format(value),
            '.'
        ].join('');
    }

    function equalFrequencyText(dimension) {
        if (dimension === 'channel') {
            return 'This is also the channel that generates the highest engagement.';
        }

        return ['This is also the post ', dimension, ' that generates the highest engagement.'].join('');
    }

    function engagementText(dimension, metric, value) {
        if (dimension === 'channel') {
            return ['The channel that generates the highest engagement in your landscape is ', metric.format(value), '.'].join('');
        }

        if (dimension === 'type' && metric.get('channel') === RivalIQ.enums.sourceDetails[RivalIQ.enums.sources.YOUTUBE].channel) {
            return 'Surprise! the best performing content type on YouTube is ... Video!';
        }


        return [
            'The post ',
            dimension,
            ' that generates the highest engagement in your landscape is ',
            metric.format(value),
            '.'
        ].join('');
    }

    function dimensionName(dimension) {
        if (dimension === 'type') {
            return 'Post Type';
        }

        return s.titleize(dimension);
    }

    // exported for testing
    const DimensionAnalysis = widgets._DimensionAnalysis = RivalIQ.views.LayoutView.mixin({
        template: '#socialPostsDimensionAnalysisLayoutTemplate',

        childView: RivalIQ.apps.Dashboard.views.Widget,

        mixins: {
            props: Marionette.Dashboard.mixins.Props,
            PostTagsReload: App.mixins.PostTagsReload,
            companySetChangeReload: RivalIQ.apps.Shell.mixins.CompanySetChangeReload
        },

        dashboardModelEvents: {
            'change': 'reload'
        },

        regions: {
            landscapeEngagementByDimension: '.x-landscape-engagement-by-dimension',
            yourActivityNoData: '.x-your-activity-no-data',
            yourActivityLegend: '.x-your-activity-legend',
            yourActivityPie: '.x-your-activity-pie'
        },

        ui: {
            insight: '.x-insight',
            insightPanel: '.x-insight-panel'
        },

        serializeData: function () {
            const me = this;

            return {
                dimensionName: dimensionName(me.getProp('dimension'))
            };
        },

        loadData: function (callback) {
            const me = this;
            const focusPostAverages = me.dashboardModel.focusPostAverages.build({
                returns: 'name',
                companyId: me.getProp('focusCompanyId')
            });
            const focusBest = me.dashboardModel.focusBest.build({
                returns: 'name',
                companyId: me.getProp('focusCompanyId')
            });
            const landscapeEngagement = me.dashboardModel.landscapeEngagement;

            // NOTE this is tangled up with `refresh` below, see that note
            me._nonBlockingLoad = $.when(landscapeEngagement.fetch(), focusBest.fetch(), focusPostAverages.fetch()).done(function (landscapeEngagement, focusBest, focusPostAverages) {
                me.landscapeEngagement = landscapeEngagement;
                me.focusBest = focusBest;
                me.focusPostAverages = focusPostAverages;
            });

            // Call this callback immediately so that the child widgets will be rendered and their metric requests will be in
            // the same batch as this loadData. See #34843
            callback();
        },

        onRenderLoaded: function () {
            const me = this;

            me._nonBlockingLoad
                .then(() => {
                    me.requestRenderedFrame(function () {
                        me.showTextInsight();

                        if (me.noFocusActivity()) {
                            me.showNoFocusActivity();
                        } else {
                            me.hideNoFocusActivity();
                        }
                    });
                });
        },

        onBeforeRenderLoaded: function () {
            const me = this;

            me.showYourActivity();
            me.showLandscapeTable();
        },

        refresh: function () {
            const me = this;

            // NOTE if the $.when fails above in `loadData` these properties
            // will not exist, and calling these methods will fail with null
            // reference exceptions (since they assume they do). this could
            // happen after a server exception if a user changes state (like
            // using a company set or adjusting the time period).
            if (me.focusPostAverages && me.focusBest && me.landscapeEngagement) {
                if (me.noFocusActivity()) {
                    me.showNoFocusActivity();
                } else {
                    me.hideNoFocusActivity();
                }

                me.showTextInsight();
            }
        },

        generateTextInsight: function () {
            const me = this;

            const topEngagement = me.landscapeEngagement.getValue({
                format: false
            });
            const topFocus = me.focusBest.getValue({
                format: false
            });

            const dimension = me.getProp('dimension');

            const insights = [];
            if (topFocus) {
                insights.push(frequencyText(dimension, me.focusBest, topFocus));
            }

            if (topEngagement) {
                if (topEngagement === topFocus) {
                    insights.push(equalFrequencyText(dimension));
                } else {
                    insights.push(engagementText(dimension, me.landscapeEngagement, topEngagement));
                }
            }

            return insights.join(' ');
        },

        showTextInsight: function () {
            const me = this;

            const insight = me.generateTextInsight();

            if (!_.isEmpty(insight)) {
                me.ui.insight.text(insight);
                me.ui.insightPanel.show();
            } else {
                me.ui.insightPanel.hide();
            }
        },

        noFocusActivity: function () {
            const me = this;
            const val = me.focusPostAverages.getValue({
                format: false
            });
            // If this dimension has been fixed val will not be an array
            return (_.isArray(val) ? _.isEmpty(val) : !val);
        },

        showNoFocusActivity: function () {
            const me = this;

            me.yourActivityNoData.show(new Marionette.ItemView({
                template: '#socialPostsDimensionAnalysisNoData'
            }));

            me.yourActivityLegend.$el.hide();
            me.yourActivityPie.$el.hide();
        },

        hideNoFocusActivity: function () {
            const me = this;
            me.yourActivityNoData.reset();
            me.yourActivityLegend.$el.show();
            me.yourActivityPie.$el.show();
        },

        showYourActivity: function () {
            const me = this;

            me.yourActivityLegend.showWidget(me.dashboardModel.getWidget('focusMetricsTable'), {
                className: 'table table-minimal'
            });
            me.yourActivityPie.showWidget(me.dashboardModel.getWidget('focusMetricsPie'));
        },

        showLandscapeTable: function () {
            const me = this;

            me.landscapeEngagementByDimension.showWidget(me.dashboardModel.getWidget('landscapeMetricsTable'), {
                className: 'table table-minimal'
            });
        }
    });

    function getYourTableMetrics(props) {

        const me = this;

        const metric = me.metric;
        const companyId = props.focusCompanyId;
        const dimensions = ['companyId', props.dimension];

        const nameMetric = metric.build({
            companyId: companyId,
            dimensions: dimensions,
            property: 'name'
        });
        nameMetric.cellView = YourActivityNameCell;

        return new RivalIQ.collections.MetricDefinitions([
            nameMetric,
            metric.build({
                property: 'count / period',
                companyId: companyId,
                dimensions: dimensions,
                valueFormat: perDayFormat,
                header: {
                    className: 'text-center'
                },
                className: 'metric-table-cell text-center'
            })
        ]);
    }

    function getLandscapeTableMetrics(props) {

        const me = this;
        const metric = me.metric;
        const dimensions = [props.dimension];

        const nameMetric = metric.build({
            property: 'name',
            dimensions: dimensions,
            hidePostsUrl: true,
            header: {
                className: 'col-sm-3'
            }
        });
        nameMetric.cellView = App.views.SearchFilterCell;
        const postWord = RivalIQ.data.channels.get(props.channel).get('postName');
        const audienceName = me.getProp('channel') === 'facebook' ? 'Page Fan' : 'Follower';

        return new RivalIQ.collections.MetricDefinitions([
            nameMetric,
            metric.build({
                name: 'Posts per Day per Company',
                property: 'count / period / companyId.count',
                dimensions: dimensions,
                valueFormat: perDayFormat,
                header: {
                    className: 'col-sm-4 text-center'
                },
                className: 'text-center metric-table-cell'
            }),
            metric.build({
                property: 'avg_engagement_rate_per_post',
                description: RivalIQ.str('social_posts_engagement_rate_by_follower', postWord, audienceName.toLocaleLowerCase(), audienceName),
                dimensions: dimensions,
                header: {
                    className: 'text-center'
                },
                cellView: function () {
                    return RivalIQ.views.metric.Bar;
                }
            })
        ]);
    }

    const DimensionWidget = RivalIQ.apps.Dashboard.models.Widget.extend({
        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                dimension: 'type',
                showDescriptions: true
            });

            if (!me.metric) {
                me.metric = new RivalIQ.models.MetricDefinition({
                    concept: RivalIQ.metrics.descriptions.SOCIAL_POSTS.id
                });

                _.extend(me, {
                    focusPostAverages: {
                        property: 'count / period',
                        companyId: props.focusCompanyId,
                        dimensions: ['companyId', props.dimension]
                    },
                    focusBest: {
                        property: 'count',
                        companyId: props.focusCompanyId,
                        dimensions: ['companyId'],
                        aggregate: 'max',
                        over: [props.dimension],
                        returns: 'name'
                    },
                    landscapeEngagement: {
                        property: 'avg_engagement_rate_per_post',
                        aggregate: 'max',
                        over: [props.dimension],
                        returns: 'name'
                    }
                });
                RivalIQ.models.MetricDefinition.build(me, me.metric, {}, 'focusPostAverages', 'focusBest', 'landscapeEngagement');
            }

            App.utils.updateMetric(me.metric, props);

            return props;
        },
        title: function () {
            const me = this;
            return `Post ${s.titleize(me.getProp('dimension'))}s`;
        },
        getExportTitle: App.utils.exportTitle,
        getExportSubtitle: App.utils.exportSubtitle,
        getPptSubtitle: App.utils.pptSubtitle,
        getSlideElements: App.utils.getSlideElements,
        propsToSerializeForExport: App.utils.widgetExportProps,
        description: function () {
            const me = this;
            return RivalIQ.strings[`social_posts_${me.getProp('dimension')}_dimension_info`];
        },
        exportFormats: [
            RivalIQ.enums.exportFormat.PDF,
            RivalIQ.enums.exportFormat.PPT,
            RivalIQ.enums.exportFormat.PNG
        ],
        view: DimensionAnalysis,
        pdfView: App.views.PdfPanel,
        pngView: App.views.PngPanel,
        viewMore: false,
        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            return [
                {
                    instanceId: 'focusMetricsTable',
                    widgetId: RivalIQ.enums.widgetTypes.METRIC_TABLE,
                    metricsCollection: getYourTableMetrics.call(me, props),
                    rebuildMetricsCollection: (props) => {
                        return getYourTableMetrics.call(me, props);
                    },
                    props: {
                        sortable: false,
                        className: 'table table-minimal',
                        orderBy: 1,
                        dontAbbreviateHeaders: true
                    },
                    neverFilter: true
                },
                {
                    instanceId: 'focusMetricsPie',
                    widgetId: RivalIQ.enums.widgetTypes.METRIC_PIE_CHART,
                    metric: me.focusPostAverages.build({
                        // we don't want the channel to be the className as that would result in the wrong colors
                        // for the pie slices (without that classname, we'll get the high contrast colors from enums/colors.js)
                        className: ''
                    }),
                    props: {
                        height: 150
                    },
                    neverFilter: true
                },
                {
                    instanceId: 'landscapeMetricsTable',
                    widgetId: RivalIQ.enums.widgetTypes.METRIC_TABLE,
                    metricsCollection: getLandscapeTableMetrics.call(me, props),
                    rebuildMetricsCollection: (props) => {
                        return getLandscapeTableMetrics.call(me, props);
                    },
                    props: {
                        orderBy: 2,
                        sortable: false,
                        dontAbbreviateHeaders: true
                    },
                    neverFilter: true
                }
            ];
        },
        filter: function () {
            const me = this;
            const dimension = me.getProp('dimension');
            // Filter this widget if we are restricting by the dimension it is
            // analyzing

            if (dimension === 'type' && _.includes([RivalIQ.enums.channels.YOUTUBE, RivalIQ.enums.channels.TIKTOK], me.getProp('channel'))) {
                return true;
            }

            return (me.filterStatic && me.getProp(dimension)) ||
                !RivalIQ.props.channelAccess(me.getProps());
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.SOCIAL_POSTS_DIMENSION_ANALYSIS, DimensionWidget);
}, window.s);
